import { useEffect, useState } from "react";
import { Bot, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import OpenAIKeyInput from "./OpenAIKeyInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { ai_model: "", openai_api_key: "", gemini_api_key: "", clear_openai_key: false, clear_gemini_key: false };

export default function AIConfigForm({ businessId, value, onChange, onSaved, masked = "", disabled = false }) {
  const [local, setLocal] = useState(EMPTY);
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const form = value || local;
  const change = (changes) => onChange ? onChange(changes) : setLocal((previous) => ({ ...previous, ...changes }));
  useEffect(() => {
    let active = true;
    setCfg(null); setError(""); setLocal(EMPTY);
    api.get(businessId ? `/businesses/${businessId}/ai-config` : "/ai-model")
      .then(({ data }) => {
        if (!active) return;
        setCfg(businessId ? data : { options: data.business_options || data.options, default_model: data.active, default_provider: data.provider });
        if (businessId) setLocal({ ...EMPTY, ai_model: data.is_default ? "" : data.ai_model });
      }).catch((failure) => { if (active) setError(apiError(failure)); });
    return () => { active = false; };
  }, [businessId]);
  const provider = cfg?.options?.find((option) => option.value === form.ai_model)?.provider || cfg?.default_provider || "openai";
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((key) => [key, form[key] ?? EMPTY[key]]));
      const { data } = await api.put(`/businesses/${businessId}/ai-config`, payload);
      setCfg(data);
      change({ openai_api_key: "", gemini_api_key: "", clear_openai_key: false, clear_gemini_key: false });
      onSaved?.();
      toast.success("Pengaturan AI bisnis disimpan");
    } catch (failure) { toast.error(apiError(failure)); }
    finally { setSaving(false); }
  };
  return <div className="rounded-xl border border-[#E2E8F0] p-4 space-y-3" data-testid="ai-config-card">
    <h3 className="flex gap-2 items-center text-sm font-bold"><Bot size={16} className="text-purple-600" />Model AI Bisnis</h3>
    <p className="text-xs text-slate-600">Model dan key untuk chatbot, ide, serta generator bisnis ini. Provider: <span data-testid="ai-config-provider">{provider === "openai" ? "OpenAI" : "Gemini"}</span>.</p>
    {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
    <div><Label className="text-xs text-slate-600">Penyedia &amp; Model</Label>
      <Select value={form.ai_model || "__default__"} onValueChange={(model) => change({ ai_model: model === "__default__" ? "" : model })} disabled={disabled || saving}>
        <SelectTrigger data-testid="ai-model-select" className="mt-1 bg-[#F1F5F9] border-[#E2E8F0]"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
          <SelectItem value="__default__">Default platform ({cfg?.default_model || "OpenAI"})</SelectItem>
          {(cfg?.options || []).map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {["gemini", "openai"].map((p) => <div key={p} className="min-w-0 space-y-1"><Label className="text-xs text-slate-600">{p === "openai" ? "OpenAI API Key" : "Gemini API Key"}</Label>
        <OpenAIKeyInput provider={p} prefix={`${p}-key`} value={form[`${p}_api_key`] || ""} masked={form[`clear_${p}_key`] ? "" : (cfg?.[`${p}_masked`] ?? (p === "openai" ? masked : value?.gemini_api_key_masked) ?? "")} unavailable={(cfg?.[`${p}_key_unavailable`] ?? form[`${p}_key_unavailable`]) && !form[`clear_${p}_key`]} disabled={disabled || saving} onChange={(key) => change({ [`${p}_api_key`]: key, [`clear_${p}_key`]: false })} onClear={() => change({ [`${p}_api_key`]: "", [`clear_${p}_key`]: true })} />
      </div>)}
    </div>
    <p className="text-[11px] text-slate-500">Key kosong mempertahankan key tersimpan. Tanpa key bisnis, aplikasi memakai key default Settings lalu environment untuk provider yang dipilih.</p>
    {businessId ? <Button type="button" onClick={save} disabled={disabled || saving || !cfg} data-testid="ai-config-save" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-xs">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <KeyRound size={14} className="mr-1" />}Simpan Pengaturan AI</Button> : <p className="text-xs text-slate-600">Pengaturan AI ikut disimpan saat bisnis ditambahkan.</p>}
  </div>;
}
